import React, { useEffect, useMemo, useState } from 'react';
import type { GalleryProps } from 'pro-gallery-lib';
import { LayoutFixer } from '@wix/pro-gallery-layout-fixer';
import { useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import { OOI_EXPERIMENTS } from '@app/experiments';
import { useResponsiveContext } from '../responsive-listener/responsive-context';
import { type ProGallery5Options } from './gallery-options';

type Props = {
  id: NonNullable<GalleryProps['id']>;
  items: NonNullable<GalleryProps['items']>;
  options: ProGallery5Options;
  children: (isPrerenderMode: boolean) => React.ReactNode;
};

export function PostListProGalleryLayoutFixer(props: Props) {
  const { rootWidth } = useResponsiveContext();

  const { dimensions } = useEnvironment();

  const nextIsDimensionsUnknown = useMemo(() => {
    const width = (dimensions.width as number | '') || rootWidth;
    const isWidthUnknown = !width || isNaN(width);

    return isWidthUnknown;
  }, [dimensions?.width, rootWidth]);

  const [isDimensionsUnknown, setIsDimensionsUnknown] = useState(nextIsDimensionsUnknown);

  const { experiments } = useExperiments();

  const isPrerenderMode = useMemo(
    () => experiments.enabled(OOI_EXPERIMENTS.USE_LAYOUT_FIXER) && isDimensionsUnknown,
    [experiments, isDimensionsUnknown],
  );

  useEffect(() => {
    if (isDimensionsUnknown !== nextIsDimensionsUnknown) {
      const timer = requestAnimationFrame(() => {
        setIsDimensionsUnknown(nextIsDimensionsUnknown);
      });

      return () => cancelAnimationFrame(timer);
    }
  }, [nextIsDimensionsUnknown, isDimensionsUnknown]);

  /**
   * Checks if all items in the `props.items` array have an empty `url` property (don't have an image). In this case do not render the layout fixer.
   */
  const containsEmptyItems = props.items.every((item) => item.url === '');

  const showLayoutFixer = !containsEmptyItems && isPrerenderMode;

  return (
    <>
      {props.children(isPrerenderMode)}
      {showLayoutFixer ? (
        <LayoutFixer id={props.id} items={props.items} isPrerenderMode options={props.options} />
      ) : undefined}
    </>
  );
}
